import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useFormik } from "formik";
import * as Yup from "yup";
import _ from "lodash";

import { GridInputs } from "../Functional";
import { Button, IconButton, Modal } from "../ui";

const toFractionalInch = value => {
  if (!value) return "";
  const inch = Math.floor(value);
  const frac = Math.round((value - inch) * 16);
  if (frac === 0) return `${inch}`;

  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(frac, 16);
  return `${inch} ${frac / divisor}/${16 / divisor}`;
};

const unitConversion = (value, unit) => {
  if (!value) return 0;

  if (unit === "in") {
    const converted = Number(value) / 2.54;
    const justified = Math.round(converted / 0.25) * 0.25;

    return justified ? justified.toFixed(2) : 0;
  } else if (unit === "cm") {
    const converted = Number(value) * 2.54;
    return converted ? converted.toFixed(2) : 0;
  }
};

const DimensionsModal = ({
  formik: modelFormik,
  fieldName,
  submitOnBlur = false,
}) => {
  const formik = useFormik({
    initialValues: {
      unit: "cm",
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: Yup.object({
      heightCm: Yup.number()
        .positive("Please enter positive number")
        .typeError("Invalid input"),
      widthCm: Yup.number()
        .positive("Please enter positive number")
        .typeError("Invalid input"),
      heightIn: Yup.number()
        .positive("Please enter positive number")
        .typeError("Invalid input"),
      widthIn: Yup.number()
        .positive("Please enter positive number")
        .typeError("Invalid input"),
    }),
  });

  const { unit } = formik.values;
  const isInches = unit === "in";
  const inputs = {
    className: "gap-8 mt-5",
    inputs: [
      {
        className: "grid grid-cols-5",
        inputs: [
          {
            label: "",
            name: "unit",
            type: "radio",
            variant: "horizontal",
            options: [
              { label: "cm", value: "cm" },
              { label: "in", value: "in" },
            ],
          },
        ],
      },
      {
        className: "grid",
        inputs: [
          {
            className: "grid grid-cols-3 gap-16",
            inputs: [
              {
                label: "Height",
                name: "heightCm",
                type: "dimension",
                disabled: isInches,
                labelleft: true,
                emptyToZero: true,
                onBlur: event => {
                  const { value } = event.target;
                  if (value) {
                    formik?.setFieldValue(
                      "heightIn",
                      unitConversion(value, "in"),
                    );
                  }
                },
              },
              {
                label: "Width",
                name: "widthCm",
                type: "dimension",
                disabled: isInches,
                labelleft: true,
                emptyToZero: true,
                onBlur: event => {
                  const { value } = event.target;
                  if (value) {
                    formik?.setFieldValue(
                      "widthIn",
                      unitConversion(value, "in"),
                    );
                  }
                },
              },
              {
                label: "Depth",
                name: "depthCm",
                type: "dimension",
                disabled: isInches,
                labelleft: true,
                emptyToZero: true,
                onBlur: event => {
                  const { value } = event.target;
                  if (value) {
                    formik?.setFieldValue(
                      "depthIn",
                      unitConversion(value, "in"),
                    );
                  }
                },
              },
            ],
          },
          {
            className: "grid grid-cols-3 gap-16",
            inputs: [
              {
                name: "heightIn",
                type: "dimension",
                disabled: !isInches,
                emptyToZero: true,
                onBlur: event => {
                  const { value } = event.target;
                  if (value) {
                    formik?.setFieldValue(
                      "heightCm",
                      unitConversion(value, "cm"),
                    );
                  }
                },
              },
              {
                name: "widthIn",
                type: "dimension",
                disabled: !isInches,
                emptyToZero: true,
                onBlur: event => {
                  const { value } = event.target;
                  if (value) {
                    formik?.setFieldValue(
                      "widthCm",
                      unitConversion(value, "cm"),
                    );
                  }
                },
              },
              {
                name: "depthIn",
                type: "dimension",
                disabled: !isInches,
                emptyToZero: true,
                onBlur: event => {
                  const { value } = event.target;
                  if (value) {
                    formik?.setFieldValue(
                      "depthCm",
                      unitConversion(value, "cm"),
                    );
                  }
                },
              },
            ],
          },
        ],
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  const onClick = closeModal => e => {
    e.preventDefault();
    const { heightCm, widthCm, depthCm, heightIn, widthIn, depthIn } =
      formik.values;
    let dimensionIn = `${toFractionalInch(heightIn)} x ${toFractionalInch(widthIn)}`;
    let dimensionCm = `${heightCm} x ${widthCm}`;
    
    if (depthIn) dimensionIn += ` x ${toFractionalInch(depthIn)}`;
    if (depthCm) dimensionCm += ` x ${depthCm}`;

    dimensionIn += " in.";
    dimensionCm += " cm";

    // prepend to previous contents after a linebreak
    const initialValue = _.get(modelFormik.values, fieldName);
    modelFormik.setFieldValue(
      fieldName,
      `${dimensionIn} (${dimensionCm})
${initialValue}`,
    );

    if (submitOnBlur) modelFormik.submitForm();
    formik.resetForm();
    closeModal();
  };

  const { heightCm, widthCm } = formik.values;
  const modalProps = {
    scale: "md",
    hideCloseButton: true,
    closeOnBackdrop: false,
    body: ({ closeModal }) => {
      return (
        <div className="flex w-full flex-col p-6">
          <div className="flex flex-row">
            <div className="flex flex-1 flex-col">
              <div className="text-xl font-bold">Add Dimension</div>
            </div>
            <div className="flex flex-col">
              <div>
                <Button
                  action="default"
                  className="mr-4"
                  label="Cancel"
                  onClick={() => {
                    closeModal();
                  }}
                />
                <Button
                  label="Add"
                  disabled={!heightCm || !widthCm}
                  onClick={onClick(closeModal)}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-8">
            <GridInputs {...inputProps} />
          </div>
        </div>
      );
    },
  };

  return (
    <Modal {...modalProps}>
      <IconButton variant="clean" title="Edit Dimension">
        <PencilSquareIcon className={`h-5 w-5 cursor-pointer text-gray-500`} />
      </IconButton>
    </Modal>
  );
};

export default DimensionsModal;
